import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOrders, getOrder, updateOrderStatus, getShops, moveOrderShop,
  uploadOrderImage, deleteOrderImage,
} from '../services/api';
import { fileToResizedDataUrl, imageUrl, MAX_IMAGES } from '../utils/image';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';
import StatusTimeline from '../components/StatusTimeline';
import Pagination from '../components/Pagination';

const STATUS_LABELS = {
  NEW: 'Νέα',
  IN_PROGRESS: 'Σε εξέλιξη',
  READY: 'Έτοιμη',
  DELIVERED: 'Παραδόθηκε',
  CANCELLED: 'Ακυρώθηκε',
};
const STATUS_BADGE = {
  NEW: 'badge-blue',
  IN_PROGRESS: 'badge-amber',
  READY: 'badge-green',
  DELIVERED: 'badge-gray',
  CANCELLED: 'badge-red',
};
const STATUS_ICON = {
  NEW: '🆕',
  IN_PROGRESS: '⚙️',
  READY: '✅',
  DELIVERED: '📦',
  CANCELLED: '❌',
};

const POLL_MS = 12000;

// The date columns are TIMESTAMP WITHOUT TIME ZONE holding Greek wall-clock
// time, so filter bounds must be sent as plain local strings. Sending
// toISOString() would shift every boundary by the UTC offset — which is why
// "Σήμερα" used to quietly include yesterday evening.
const naive = (d) => format(d, "yyyy-MM-dd'T'HH:mm:ss");
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// Date range presets
const dateStartOf = (key) => {
  const now = new Date();
  if (key === 'today') return naive(startOfDay(now));
  if (key === 'yesterday') {
    const d = startOfDay(now); d.setDate(d.getDate() - 1); return naive(d);
  }
  if (key === 'last7') {
    const d = startOfDay(now); d.setDate(d.getDate() - 6); return naive(d);
  }
  if (key === 'month') return naive(new Date(now.getFullYear(), now.getMonth(), 1));
  return null;
};
const dateEndOf = (key) => {
  const now = new Date();
  if (key === 'yesterday') return naive(startOfDay(now)); // up to today start
  return null; // 'today', 'last7', 'month', 'all' → no upper bound
};

const PAGE_SIZE = 100;

export default function MyOrders() {
  const navigate = useNavigate();
  const { selectedShop } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({}); // { [orderId]: detailObject }
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  const [dateRange, setDateRange] = useState('today'); // today | yesterday | last7 | month | all | custom
  // Custom range (dateRange === 'custom')
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Which date the range applies to: when it was placed, or the pickup date
  const [dateField, setDateField] = useState('created_at');
  const [searchInput, setSearchInput] = useState(''); // what the user is typing
  const [search, setSearch] = useState('');           // debounced value sent to backend
  const [cancellingId, setCancellingId] = useState(null);
  // Moving an order's pickup to another branch
  const [allShops, setAllShops] = useState([]);
  const [shopMoveFor, setShopMoveFor] = useState(null);
  const [shopTarget, setShopTarget] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  // Used to skip overlapping polls
  const inFlightRef = useRef(false);

  // Debounce: update `search` 300ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when any filter changes
  useEffect(() => { setPage(1); }, [selectedShop, statusFilter, dateRange, search, customFrom, customTo, dateField]);

  const load = useCallback(async (silent = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (selectedShop) params.shop_id = selectedShop.id;
      if (statusFilter) params.status = statusFilter;
      if (dateRange === 'custom') {
        if (customFrom) params.from = `${customFrom}T00:00:00`;
        if (customTo) {
          // "to" is exclusive, so push it to the start of the following day
          const d = new Date(`${customTo}T00:00:00`);
          d.setDate(d.getDate() + 1);
          params.to = naive(d);
        }
        if (params.from || params.to) params.date_field = dateField;
      } else if (dateRange !== 'all') {
        const from = dateStartOf(dateRange);
        const to = dateEndOf(dateRange);
        if (from) params.from = from;
        if (to) params.to = to;
      }
      if (search.trim()) params.search = search.trim();
      const r = await getOrders(params);
      // Paginated response shape: { orders, total, page, page_size }
      const data = r.data;
      const list = Array.isArray(data) ? data : (data.orders || []);
      const newTotal = Array.isArray(data) ? list.length : (data.total || 0);
      setOrders(list);
      setTotal(newTotal);
      // If we requested a page that doesn't exist (e.g. orders cancelled), drop back
      if (!Array.isArray(data) && list.length === 0 && newTotal > 0 && page > 1) {
        const lastPage = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
        if (page !== lastPage) setPage(lastPage);
      }
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
    } finally {
      if (!silent) setLoading(false);
      inFlightRef.current = false;
    }
  }, [selectedShop, statusFilter, dateRange, search, page, customFrom, customTo, dateField]);

  // Initial + filter-change load
  useEffect(() => { load(); }, [load]);

  // Polling every 12s — this is what makes the Επεξεργασία button disappear
  // on its own within ~12s of the factory pressing "Ξεκινώ".
  useEffect(() => {
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Re-fetch detail if the expanded card changes status OR is edited under it
  useEffect(() => {
    if (!expandedId) return;
    const order = orders.find((o) => o.id === expandedId);
    const detail = details[expandedId];
    if (order && detail && (
      order.last_status_change_at !== detail.last_status_change_at ||
      order.edited_at !== detail.edited_at
    )) {
      loadDetail(expandedId);
    }
    // eslint-disable-next-line
  }, [orders]);

  const loadDetail = async (id) => {
    try {
      const r = await getOrder(id);
      setDetails((d) => ({ ...d, [id]: r.data }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!details[id]) loadDetail(id);
  };

  // Shop list for the "move pickup" picker — every active branch, not just
  // the ones this user works at, since the point is handing it elsewhere.
  useEffect(() => {
    getShops().then((r) => setAllShops(r.data)).catch(() => setAllShops([]));
  }, []);

  const confirmShopMove = async () => {
    if (!shopMoveFor || !shopTarget) return toast.error('Επιλέξτε κατάστημα');
    const order = shopMoveFor;
    setCancellingId(order.id);
    try {
      const r = await moveOrderShop(order.id, Number(shopTarget));
      toast.success(`Παραλαβή από ${r.data.to_shop}`);
      setShopMoveFor(null);
      setShopTarget('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setCancellingId(null);
    }
  };

  const addPhotos = async (order, e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (files.length === 0) return;
    const existing = (details[order.id]?.images || []).length;
    const room = MAX_IMAGES - existing;
    if (room <= 0) return toast.error(`Έως ${MAX_IMAGES} φωτογραφίες`);

    setPhotoBusy(true);
    try {
      let done = 0;
      for (const f of files.slice(0, room)) {
        if (!f.type.startsWith('image/')) continue;
        const dataUrl = await fileToResizedDataUrl(f);
        await uploadOrderImage(order.id, dataUrl);
        done += 1;
      }
      if (done > 0) toast.success(done === 1 ? 'Η φωτογραφία προστέθηκε' : `${done} φωτογραφίες προστέθηκαν`);
      loadDetail(order.id);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα ανεβάσματος');
      loadDetail(order.id);
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async (order, img) => {
    if (!window.confirm('Διαγραφή φωτογραφίας;')) return;
    setPhotoBusy(true);
    try {
      await deleteOrderImage(order.id, img.id);
      loadDetail(order.id);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setPhotoBusy(false);
    }
  };

  const markDelivered = async (order, e) => {
    e.stopPropagation();
    if (!window.confirm(`Ο πελάτης παρέλαβε την παραγγελία #${order.id};`)) return;
    setCancellingId(order.id);
    try {
      await updateOrderStatus(order.id, 'DELIVERED', null);
      toast.success('Καταχωρήθηκε ως παραδομένη');
      load();
      if (expandedId === order.id) loadDetail(order.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setCancellingId(null);
    }
  };

  const cancelOrder = async (order, e) => {
    e.stopPropagation();
    if (!window.confirm(`Ακύρωση παραγγελίας #${order.id}; Δεν αναιρείται.`)) return;
    setCancellingId(order.id);
    try {
      await updateOrderStatus(order.id, 'CANCELLED', null);
      toast.success('Η παραγγελία ακυρώθηκε');
      load();
      if (expandedId === order.id) loadDetail(order.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setCancellingId(null);
    }
  };

  const formatWhen = (iso) => format(new Date(iso), 'd MMM, HH:mm', { locale: el });
  const formatFullWhen = (iso) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: el });

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>📋 Οι Παραγγελίες μου</h2>
            <p>{selectedShop ? <>Κατάστημα: <strong>{selectedShop.name}</strong></> : 'Όλες οι παραγγελίες'}</p>
          </div>
          <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <input
              className="form-input"
              placeholder="🔍 #ID ή πελάτης..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 180, padding: '6px 10px', margin: 0 }}
            />
            <select
              className="form-select"
              value={dateRange}
              onChange={(e) => {
                const v = e.target.value;
                setDateRange(v);
                // Opening the custom range with nothing in it would show
                // everything; start it on today so the result makes sense.
                if (v === 'custom' && !customFrom && !customTo) {
                  const t = format(new Date(), 'yyyy-MM-dd');
                  setCustomFrom(t);
                  setCustomTo(t);
                }
              }}
              style={{ width: 150, padding: '6px 10px' }}
            >
              <option value="today">Σήμερα</option>
              <option value="yesterday">Χθες</option>
              <option value="last7">Τελευταίες 7</option>
              <option value="month">Αυτόν τον μήνα</option>
              <option value="all">Όλες</option>
              <option value="custom">📅 Επιλογή ημερομηνιών…</option>
            </select>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 180, padding: '6px 10px' }}>
              <option value="">Όλες οι καταστάσεις</option>
              <option value="NEW">{STATUS_ICON.NEW} {STATUS_LABELS.NEW}</option>
              <option value="IN_PROGRESS">{STATUS_ICON.IN_PROGRESS} {STATUS_LABELS.IN_PROGRESS}</option>
              <option value="READY">{STATUS_ICON.READY} {STATUS_LABELS.READY}</option>
              <option value="DELIVERED">{STATUS_ICON.DELIVERED} {STATUS_LABELS.DELIVERED}</option>
              <option value="CANCELLED">{STATUS_ICON.CANCELLED} {STATUS_LABELS.CANCELLED}</option>
            </select>
            <button className="btn btn-secondary" onClick={() => load()} title="Ανανέωση">↻</button>
          </div>
        </div>

        {dateRange === 'custom' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            marginTop: 10, padding: '10px 12px',
            background: 'var(--blue-light, #eff6ff)', border: '1px solid #bfdbfe', borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>📅 Από</span>
            <input
              type="date"
              className="form-input"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={{ width: 160, padding: '5px 8px', margin: 0 }}
            />
            <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>έως</span>
            <input
              type="date"
              className="form-input"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              style={{ width: 160, padding: '5px 8px', margin: 0 }}
            />
            <select
              className="form-select"
              value={dateField}
              onChange={(e) => setDateField(e.target.value)}
              style={{ width: 190, padding: '5px 8px' }}
              title="Σε ποια ημερομηνία εφαρμόζεται το εύρος"
            >
              <option value="created_at">ημ. καταχώρησης</option>
              <option value="requested_for">ημ. παραλαβής</option>
            </select>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const t = format(new Date(), 'yyyy-MM-dd');
                setCustomFrom(t); setCustomTo(t);
              }}
            >Σήμερα</button>
          </div>
        )}
      </div>
      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : orders.length === 0 ? (
          <div className="card"><div className="card-body">
            <div className="empty-state"><div>📋</div><p>{search.trim() || statusFilter || dateRange !== 'all' ? 'Δεν βρέθηκαν παραγγελίες' : 'Δεν υπάρχουν παραγγελίες'}</p></div>
          </div></div>
        ) : (
          <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orders.map((o) => {
              const isExpanded = expandedId === o.id;
              const detail = details[o.id];
              // The pickup was moved to another branch. We keep the order on
              // the list so the counter staff can still look it up, but every
              // control goes — photos and cancel included, since they hang off
              // canCancel. The backend refuses this branch anyway.
              const gone = !!o.moved_away;
              const canCancel = ['NEW', 'IN_PROGRESS', 'READY'].includes(o.status) && !gone;
              // Editing is only offered while the order is still NEW. The
              // backend re-checks this, so a stale button is harmless.
              const canEdit = o.status === 'NEW' && !gone;
              const wasEdited = (o.edit_count || 0) > 0;
              return (
                <div key={o.id} className="card" style={{
                  overflow: 'visible',
                  background: gone ? 'var(--gray-50)' : undefined,
                }}>
                  <div
                    onClick={() => toggleExpand(o.id)}
                    style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <div style={{ fontSize: 22 }}>{STATUS_ICON[o.status]}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>#{o.id}</span>
                        <span style={{ fontWeight: 500 }}>{o.customer_name || '(χωρίς όνομα)'}</span>
                        <span className={'badge ' + STATUS_BADGE[o.status]}>{STATUS_LABELS[o.status]}</span>
                        {gone && (
                          <span className="badge badge-gray" title="Η παραλαβή μεταφέρθηκε σε άλλο κατάστημα">
                            🏪 Μεταφέρθηκε{o.shop_name ? ` στο ${o.shop_name}` : ''}
                          </span>
                        )}
                        {o.image_count > 0 && (
                          <span className="badge badge-gray" title={`${o.image_count} φωτογραφίες`}>
                            📷 {o.image_count}
                          </span>
                        )}
                        {wasEdited && (
                          <span
                            className="badge badge-amber"
                            title={o.edited_at ? `Τελευταία τροποποίηση: ${formatFullWhen(o.edited_at)}` : undefined}
                          >
                            ✏️ Τροποποιήθηκε{o.edit_count > 1 ? ` ×${o.edit_count}` : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                        🏭 {o.factory_names || '—'} · {o.item_count} {o.item_count === 1 ? 'προϊόν' : 'προϊόντα'}
                        {o.item_count > 0 && !['DELIVERED', 'CANCELLED'].includes(o.status) && (
                          <> · <strong style={{ color: o.ready_count === o.item_count ? 'var(--green-dark)' : 'var(--gray-600)' }}>
                            {o.ready_count}/{o.item_count} έτοιμα
                          </strong></>
                        )} ·
                        παραλαβή: {formatFullWhen(o.requested_for)} ·
                        δημιουργία: {formatWhen(o.created_at)}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, color: 'var(--blue-dark)', fontSize: 15 }}>
                      €{parseFloat(o.grand_total).toFixed(2)}
                    </div>
                    {o.status === 'READY' && !gone && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => markDelivered(o, e)}
                        disabled={cancellingId === o.id}
                        title="Ο πελάτης την παρέλαβε"
                      >
                        📦 Παραδόθηκε
                      </button>
                    )}
                    {canEdit && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/edit-order/${o.id}`); }}
                        title="Επεξεργασία παραγγελίας"
                      >
                        ✏️ Επεξεργασία
                      </button>
                    )}
                    {canCancel && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => { e.stopPropagation(); setShopMoveFor(o); setShopTarget(''); }}
                        disabled={cancellingId === o.id}
                        title="Αλλαγή καταστήματος παραλαβής"
                      >🏪</button>
                    )}
                    {canCancel && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => cancelOrder(o, e)}
                        disabled={cancellingId === o.id}
                      >
                        Ακύρωση
                      </button>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => { e.stopPropagation(); window.open(`/print/${o.id}`, '_blank'); }}
                      title="Εκτύπωση"
                    >🖨</button>
                    <div style={{ color: 'var(--gray-400)', fontSize: 18 }}>{isExpanded ? '▴' : '▾'}</div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '14px 18px', borderTop: '1px solid var(--gray-100)', background: 'var(--gray-50)' }}>
                      {!detail ? (
                        <div className="page-loading"><span className="loading-spinner dark" /></div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
                            <InfoBlock label="Πελάτης" value={detail.customer_name || '(χωρίς όνομα)'} />
                            {detail.customer_phone && <InfoBlock label="Τηλέφωνο" value={detail.customer_phone} />}
                            <InfoBlock label="Κατάστημα" value={detail.shop_name} />
                            <InfoBlock label="Παραλαβή" value={formatFullWhen(detail.requested_for)} />
                            {detail.created_by_worker_name && <InfoBlock label="Παραγγέλθηκε από" value={detail.created_by_worker_name} />}
                            <InfoBlock label="Δημιουργήθηκε" value={formatFullWhen(detail.created_at) + (detail.created_by_name ? ` από ${detail.created_by_name}` : '')} />
                            {detail.edited_at && (
                              <InfoBlock
                                label="Τροποποιήθηκε"
                                value={formatFullWhen(detail.edited_at) + (detail.edited_by_worker_name ? ` από ${detail.edited_by_worker_name}` : '')}
                              />
                            )}
                          </div>

                          {detail.customer_notes && (
                            <div style={{ padding: 10, background: 'white', borderRadius: 8, fontSize: 13, color: 'var(--gray-700)', marginBottom: 14 }}>
                              📝 {detail.customer_notes}
                            </div>
                          )}

                          {/* Photos — the shop can still add or remove them
                              while the order is open. */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                              Φωτογραφίες ({(detail.images || []).length}/{MAX_IMAGES})
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                              {(detail.images || []).map((img) => (
                                <div key={img.id} style={{ position: 'relative' }}>
                                  <a href={imageUrl(img.access_key)} target="_blank" rel="noreferrer">
                                    <img
                                      src={imageUrl(img.access_key)}
                                      alt=""
                                      style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)', display: 'block', background: 'white' }}
                                    />
                                  </a>
                                  {canCancel && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); removePhoto(o, img); }}
                                      disabled={photoBusy}
                                      title="Διαγραφή"
                                      style={{
                                        position: 'absolute', top: -6, right: -6,
                                        width: 22, height: 22, borderRadius: '50%',
                                        border: 'none', background: 'var(--red, #ef4444)', color: 'white',
                                        cursor: 'pointer', fontSize: 13, lineHeight: '22px', padding: 0,
                                      }}
                                    >×</button>
                                  )}
                                </div>
                              ))}
                              {canCancel && (detail.images || []).length < MAX_IMAGES && (
                                <label
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: 88, height: 88, borderRadius: 8,
                                    border: '1px dashed var(--gray-300)', background: 'white',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    cursor: photoBusy ? 'wait' : 'pointer', color: 'var(--gray-500)', fontSize: 11, gap: 4,
                                  }}
                                >
                                  <span style={{ fontSize: 20 }}>📷</span>
                                  {photoBusy ? '...' : 'Προσθήκη'}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    disabled={photoBusy}
                                    onChange={(e) => addPhotos(o, e)}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              )}
                              {(detail.images || []).length === 0 && !canCancel && (
                                <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>—</div>
                              )}
                            </div>
                          </div>

                          {/* Per-factory progress — an order may be spread
                              across several factories, each working its own
                              share independently. */}
                          {(detail.parts || []).length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                Εργοστάσια
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {detail.parts.map((p) => {
                                  const done = p.item_count > 0 && p.ready_count === p.item_count;
                                  return (
                                    <div key={p.id} style={{
                                      display: 'flex', alignItems: 'center', gap: 10,
                                      background: 'white', borderRadius: 8, padding: '8px 12px',
                                      borderLeft: `3px solid ${done ? '#10b981' : p.started_at ? '#f59e0b' : 'var(--gray-300)'}`,
                                    }}>
                                      <span style={{ fontSize: 15 }}>{done ? '✅' : p.started_at ? '⚙️' : '🆕'}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 500, fontSize: 13 }}>{p.factory_name}</div>
                                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                                          {p.ready_count}/{p.item_count} έτοιμα
                                          {p.started_by_worker_name && ` · ανέλαβε ${p.started_by_worker_name}`}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Προϊόντα</div>
                            <div style={{ background: 'white', borderRadius: 8, padding: '6px 12px' }}>
                              {detail.items.map((it) => (
                                <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                                  <span style={{ fontSize: 15, marginRight: 10 }}>
                                    {it.status === 'READY' ? '✅' : '⏳'}
                                  </span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      fontWeight: 500,
                                      color: it.status === 'READY' ? 'var(--gray-500)' : 'var(--gray-900)',
                                    }}>{it.product_name}</div>
                                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                                      🏭 {it.factory_name} · €{parseFloat(it.unit_price).toFixed(2)} × {parseFloat(it.quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}
                                      {it.notes && <span> · 📝 {it.notes}</span>}
                                    </div>
                                  </div>
                                  <strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(it.total_price).toFixed(2)}</strong>
                                </div>
                              ))}
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 4px', fontWeight: 700, color: 'var(--blue-dark)' }}>
                                <span>Σύνολο</span>
                                <span>€{parseFloat(detail.grand_total).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Πρόοδος</div>
                            <StatusTimeline currentStatus={detail.status} history={detail.history || []} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Pagination
            page={page}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => { setPage(p); setExpandedId(null); }}
          />
          </>
        )}
      </div>

      {/* Move the pickup to another branch */}
      {shopMoveFor && (
        <div onClick={() => setShopMoveFor(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 460 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', fontWeight: 600 }}>
              Αλλαγή καταστήματος παραλαβής
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 14, marginBottom: 12 }}>
                Παραγγελία <strong>#{shopMoveFor.id}</strong> — {shopMoveFor.customer_name || '(χωρίς όνομα)'}
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  Τώρα: {shopMoveFor.shop_name}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Νέο κατάστημα παραλαβής *</label>
                <select className="form-select" value={shopTarget} onChange={(e) => setShopTarget(e.target.value)} autoFocus>
                  <option value="">-- Επιλέξτε --</option>
                  {allShops.filter((s) => s.id !== shopMoveFor.shop_id).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                ⚠️ Μετά τη μεταφορά η παραγγελία θα εμφανίζεται στο νέο κατάστημα και δεν θα είναι
                πλέον ορατή από το τρέχον. Τα εργοστάσια δεν επηρεάζονται.
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShopMoveFor(null)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={confirmShopMove} disabled={!shopTarget}>🏪 Μεταφορά</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--gray-800)' }}>{value}</div>
    </div>
  );
}
